import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartCtx = createContext(null);
const KEY = "qdn_cart_v2";
const OLD_KEY = "lcds_cart_v1";

export const lineId = (productId, variantId) => `${productId}__${variantId || ""}`;

function normalize(list) {
  return (Array.isArray(list) ? list : [])
    .filter((i) => i && i.product_id)
    .map((i) => ({
      line_id: i.line_id || lineId(i.product_id, i.variant_id),
      product_id: i.product_id,
      variant_id: i.variant_id || null,
      variant_label: i.variant_label || "",
      name: i.name,
      price: Number(i.price) || 0,
      image: i.image || "",
      qty: Number(i.qty) || 1,
    }));
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const cur = localStorage.getItem(KEY);
      if (cur) return normalize(JSON.parse(cur));
      const old = localStorage.getItem(OLD_KEY);
      return old ? normalize(JSON.parse(old)) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const add = (p, variant = null) => {
    const lid = lineId(p.id, variant?.id);
    const price = variant ? Number(variant.price) : Number(p.price);
    const label = variant ? variant.label || "" : p.unit || "";
    setItems((prev) => {
      const ex = prev.find((i) => i.line_id === lid);
      if (ex) return prev.map((i) => (i.line_id === lid ? { ...i, qty: i.qty + 1 } : i));
      return [
        ...prev,
        {
          line_id: lid,
          product_id: p.id,
          variant_id: variant?.id || null,
          variant_label: label,
          name: p.name,
          price,
          image: p.image,
          qty: 1,
        },
      ];
    });
  };

  const inc = (lid) =>
    setItems((prev) => prev.map((i) => (i.line_id === lid ? { ...i, qty: i.qty + 1 } : i)));

  const dec = (lid) =>
    setItems((prev) =>
      prev
        .map((i) => (i.line_id === lid ? { ...i, qty: i.qty - 1 } : i))
        .filter((i) => i.qty > 0),
    );

  const remove = (lid) => setItems((prev) => prev.filter((i) => i.line_id !== lid));
  const clear = () => setItems([]);

  const qtyOf = (productId, variantId) =>
    items.find((i) => i.line_id === lineId(productId, variantId))?.qty || 0;

  const { subtotal, count } = useMemo(
    () => ({
      subtotal: items.reduce((s, i) => s + i.price * i.qty, 0),
      count: items.reduce((s, i) => s + i.qty, 0),
    }),
    [items],
  );

  return (
    <CartCtx.Provider
      value={{ items, add, inc, dec, remove, clear, subtotal, count, open, setOpen, qtyOf }}
    >
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
